/**
 * Обёртка над fetch (docs/05 §1). Все запросы идут на `/api` с cookie-сессией;
 * ошибки приводятся к ApiError с кодом контракта.
 */

import { ApiError, type ApiErrorDetailsItem } from '../lib/errors';
import { logger } from '../lib/logger';

const API_BASE = '/api';

/** Пути, для которых 401 — ожидаемый ответ, а не повод уводить на логин. */
const AUTH_EXEMPT_PREFIXES = ['/auth/'] as const;
const AUTH_EXEMPT_EXACT = ['/me'] as const;

export type QueryValue = string | number | boolean | null | undefined;

export type QueryParams = Readonly<Record<string, QueryValue>>;

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export type RequestOptions = {
  readonly method?: HttpMethod;
  readonly query?: QueryParams;
  readonly body?: unknown;
  readonly formData?: FormData;
  readonly signal?: AbortSignal;
};

export function buildQuery(params: QueryParams | undefined): string {
  if (!params) {
    return '';
  }
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const serialized = search.toString();
  return serialized.length > 0 ? `?${serialized}` : '';
}

function isAuthExempt(path: string): boolean {
  if (AUTH_EXEMPT_EXACT.includes(path as (typeof AUTH_EXEMPT_EXACT)[number])) {
    return true;
  }
  return AUTH_EXEMPT_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/** Единый редирект на логин вместо «мигания» защищённых экранов (docs/06 §7). */
function redirectToLogin(): void {
  const { pathname, search } = window.location;
  const next = encodeURIComponent(`${pathname}${search}`);
  window.location.assign(`/login?next=${next}`);
}

type ErrorPayload = {
  error?: {
    code?: string;
    message?: string;
    details?: readonly ApiErrorDetailsItem[];
    requestId?: string;
  };
};

function parseErrorPayload(raw: string): ErrorPayload {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed as ErrorPayload;
    }
  } catch {
    // Тело не JSON — используем статус как единственный сигнал.
  }
  return {};
}

function toApiError(response: Response, raw: string): ApiError {
  const payload = parseErrorPayload(raw);
  const code = payload.error?.code ?? `HTTP_${response.status}`;
  const message = payload.error?.message ?? response.statusText ?? 'Не удалось выполнить запрос.';
  return new ApiError({
    code,
    message,
    status: response.status,
    details: payload.error?.details ?? [],
    requestId: payload.error?.requestId ?? null,
  });
}

async function readBody(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return '';
  }
}

async function sendRequest(path: string, options: RequestOptions): Promise<Response> {
  const { method = 'GET', query, body, formData, signal } = options;
  const isForm = formData !== undefined;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined && !isForm) {
    headers['Content-Type'] = 'application/json; charset=utf-8';
  }
  try {
    return await fetch(`${API_BASE}${path}${buildQuery(query)}`, {
      method,
      headers,
      credentials: 'include',
      body: isForm ? formData : body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      throw cause;
    }
    logger.warn('network failure', path, cause);
    throw ApiError.network('Нет связи с сервером. Проверьте соединение и повторите.');
  }
}

async function ensureOk(response: Response, path: string): Promise<void> {
  if (response.ok) {
    return;
  }
  const raw = await readBody(response);
  if (response.status === 401 && !isAuthExempt(path)) {
    redirectToLogin();
  }
  throw toApiError(response, raw);
}

/** JSON-запрос. Для ответов без тела используйте `apiRequestVoid`. */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await sendRequest(path, options);
  await ensureOk(response, path);
  const raw = await readBody(response);
  if (raw.length === 0) {
    throw new ApiError({ code: 'EMPTY_RESPONSE', message: 'Сервер вернул пустой ответ.', status: response.status });
  }
  const parsed: unknown = JSON.parse(raw);
  return parsed as T;
}

/** Запрос, ответ которого не нужен (204 No Content). */
export async function apiRequestVoid(path: string, options: RequestOptions = {}): Promise<void> {
  const response = await sendRequest(path, options);
  await ensureOk(response, path);
}

/** Коллекция в формате `{ items, total?, limit?, offset? }` (docs/05 §1). */
export type ItemList<T> = {
  readonly items: readonly T[];
  readonly total?: number;
  readonly limit?: number;
  readonly offset?: number;
};

/**
 * Разворачивает контейнер коллекции.
 *
 * Списочные эндпоинты отдают `{ items: [...] }` (docs/05 §1), а не голый
 * массив. Раньше часть клиентов ждала массив напрямую — и падала с
 * `.map is not a function`. Разворот терпим к обоим формам.
 */
export function unwrapItems<T>(payload: ItemList<T> | readonly T[] | null | undefined): T[] {
  if (payload === null || payload === undefined) return [];
  if (Array.isArray(payload)) return [...(payload as readonly T[])];
  return [...((payload as ItemList<T>).items ?? [])];
}

/** JSON-запрос к списочному эндпоинту: всегда возвращает массив. */
export async function apiRequestList<T>(path: string, options: RequestOptions = {}): Promise<T[]> {
  return unwrapItems(await apiRequest<ItemList<T> | T[]>(path, options));
}

/**
 * Разворачивает огибающую одиночного ресурса (`{ issue: {...} }` → `{...}`).
 *
 * Часть эндпоинтов отдаёт ресурс в огибающей (`{ issue }`, `{ comment }`,
 * `{ filter }`, `{ project }`, `{ attachment }`, `{ user }`), а часть —
 * «голым» (docs/05 §1). Разворот терпим к обоим формам: если в объекте есть
 * ключ `key` — берём его, иначе считаем, что ресурс уже развёрнут.
 *
 * Без этого клиент читал `undefined` вместо полей задачи: панель задачи
 * падала на `issue.status.name`, а списки оставались пустыми.
 */
export function unwrapResource<T>(payload: unknown, key: string): T {
  if (payload !== null && typeof payload === 'object' && key in payload) {
    return (payload as Record<string, T>)[key] as T;
  }
  return payload as T;
}

/** JSON-запрос к ресурсу в огибающей `{ [key]: resource }` или «голому». */
export async function apiRequestResource<T>(
  path: string,
  key: string,
  options: RequestOptions = {},
): Promise<T> {
  return unwrapResource<T>(await apiRequest<unknown>(path, options), key);
}
